import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, Phone, Mail, Globe, Edit2, ArrowLeft, 
  Users, Home, FileText, Image as ImageIcon, RefreshCw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { condoInfoAPI } from '../services/api';
import Toast, { useToast } from '../components/Toast';

interface CondoInfo {
  name: string;
  logo?: string;
  address: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  phone: string;
  email: string;
  website?: string;
  adminName: string;
  totalUnits: number;
  totalResidents: number;
  address_data?: Record<string, string>;
  latitude?: number | null;
  longitude?: number | null;
  fee_calc_method?: 'per_unit' | 'per_sqm' | 'variable';
  fee_rate?: number | null;
  variable_distribution?: 'by_unit' | 'by_area';
}

export default function InformacionCondominio() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [info, setInfo] = useState<CondoInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const { isAdmin, isDirective } = useAuth();
  const { toasts, removeToast, success, error: showError } = useToast();
  const canManageFees = isAdmin() || isDirective();

  useEffect(() => {
    loadInfo();
  }, []);

  const loadInfo = async () => {
    setLoading(true);
    setError(false);
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      if (!condoId) {
        setError(true);
        setLoading(false);
        return;
      }
      const response = await condoInfoAPI.get(condoId);
      const condoData = response.data?.data || response.data;
      if (condoData) {
        setInfo(condoData);
      } else {
        setError(true);
      }
    } catch (err) {
      console.error('Error loading condo info:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const condoId = userData.condoId;
      const feeMethod = info?.fee_calc_method || 'per_unit';
      if (feeMethod === 'per_sqm' && !(Number(info?.fee_rate) > 0)) {
        showError('Para calcular por m² debes indicar una cuota mayor que 0.');
        return;
      }
      if (feeMethod === 'variable' && !info?.variable_distribution) {
        showError('Selecciona cómo repartir la cuota variable.');
        return;
      }
      const payload = {
        ...info,
        fee_calc_method: feeMethod,
        fee_rate: feeMethod === 'variable' ? null : (info?.fee_rate === null || info?.fee_rate === undefined ? null : Number(info.fee_rate)),
        variable_distribution: feeMethod === 'variable' ? info?.variable_distribution : undefined,
      };
      await condoInfoAPI.update(condoId, payload);
      setInfo((prev) => (prev ? { ...prev, ...payload } : prev));
      setIsEditing(false);
      success('Información guardada correctamente');
    } catch (err) {
      console.error('Error saving condo info:', err);
      const responseError = err as { response?: { data?: { message?: string; error?: string; code?: string } }; message?: string };
      showError(responseError.response?.data?.message || responseError.response?.data?.error || responseError.message || 'Error al guardar');
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)] flex items-center justify-center" style={{ fontFamily: 'Inter, sans-serif' }}>
        <p className="text-gray-500">Cargando información...</p>
      </div>
    );
  }

  if (error || !info) {
    return (
      <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
        <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-bold text-[#2D3748]">Información del Condominio</h1>
            </div>
          </div>
        </header>
        <main className="p-6 max-w-2xl mx-auto text-center">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-12">
            <p className="text-gray-500 mb-4">No se pudo cargar la información del condominio.</p>
            <button 
              onClick={loadInfo}
              className="px-6 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" /> Reintentar
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-[var(--v-base-bg)]" style={{ fontFamily: 'Inter, sans-serif' }}>
      <header className="sticky top-0 bg-white border-b border-gray-200 z-50">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/home/dashboard')} className="p-2 text-[#2D3748] hover:bg-gray-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-xl font-bold text-[#2D3748]">Información del Condominio</h1>
          </div>
          {canManageFees && !isEditing && (
            <button onClick={() => setIsEditing(true)} className="px-4 py-2 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4] flex items-center gap-2">
              <Edit2 className="w-4 h-4" /> Editar
            </button>
          )}
        </div>
      </header>

      <main className="p-6 max-w-2xl mx-auto">
        {/* Logo Area */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 mb-6 text-center">
          <div className="w-24 h-24 bg-[#36CCD8]/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
            {info.logo ? (
              <img src={info.logo} alt="Logo" className="w-full h-full object-contain" />
            ) : (
              <div className="flex gap-0.5">
                <Home className="w-10 h-10 text-[#36CCD8]" />
                <Home className="w-10 h-10 text-[#36CCD8] -ml-4" />
              </div>
            )}
          </div>
          {isEditing ? (
            <div className="space-y-2">
              <input 
                type="text" 
                value={info.name}
                onChange={(e) => setInfo({...info, name: e.target.value})}
                className="text-2xl font-bold text-[#2D3748] text-center w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
              />
              <button className="text-sm text-[#36CCD8] flex items-center justify-center gap-1 mx-auto">
                <ImageIcon className="w-4 h-4" /> Cambiar logo
              </button>
            </div>
          ) : (
            <h2 className="text-2xl font-bold text-[#2D3748]">{info.name}</h2>
          )}
        </div>

        {/* Info Cards */}
        <div className="space-y-4">
          {/* Contact Info */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-bold text-[#2D3748] mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[#36CCD8]" /> Información General
            </h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-500">Dirección</label>
                {isEditing ? (
                  <input 
                    type="text" 
                    value={info.address}
                    onChange={(e) => setInfo({...info, address: e.target.value})}
                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                  />
                ) : (
                  <>
                  <p className="text-[#2D3748] flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    {info.address_data?.formatted || [info.address, info.city, info.state, info.country, info.zipCode].filter(Boolean).join(', ')}
                  </p>
                  {info.address_data && Object.keys(info.address_data).length > 0 && (
                    <div className="mt-3 rounded-xl bg-gray-50 border border-gray-100 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Partes de la dirección</p>
                      <div className="grid grid-cols-2 gap-3">
                        {(() => { const L: Record<string,string> = { establishment:'Establecimiento', route:'Ruta', street_number:'Número', intersection:'Intersección', neighborhood:'Colonia/Barrio', sublocality:'Sublocalidad', sublocality_level_1:'Sublocalidad', locality:'Localidad/Ciudad', administrative_area_level_2:'Municipio/Condado', administrative_area_level_1:'Estado/Provincia', postal_code:'Código postal', postal_code_suffix:'Sufijo CP', country:'País', country_code:'Código de país', premise:'Edificio', subpremise:'Interior/Piso', floor:'Piso', ward:'Distrito', post_box:'Apartado postal' }; const O = ['establishment','premise','route','street_number','intersection','subpremise','floor','neighborhood','sublocality','sublocality_level_1','locality','administrative_area_level_2','administrative_area_level_1','postal_code','postal_code_suffix','ward','post_box','country','country_code']; const ad = info.address_data || {}; return Object.keys(ad).filter(k => k !== 'formatted' && ad[k]).sort((a,b) => { const ia = O.indexOf(a), ib = O.indexOf(b); return (ia<0?99:ia)-(ib<0?99:ib); }).map(k => [L[k] || k.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase()), k]); })().map(([label, key]) => (
                          <div key={key}>
                            <p className="text-xs text-gray-400">{label}</p>
                            <p className="text-sm text-[#2D3748]">{info.address_data?.[key] || '—'}</p>
                          </div>
                        ))}
                      </div>
                      {info.latitude != null && info.longitude != null && (
                        <a href={`https://www.google.com/maps?q=${encodeURIComponent(`${info.latitude},${info.longitude}`)}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#36CCD8]">
                          <MapPin className="w-4 h-4" /> Ver en Google Maps
                        </a>
                      )}
                    </div>
                  )}
                  </>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-500">Teléfono</label>
                  {isEditing ? (
                    <input type="tel" value={info.phone} onChange={(e) => setInfo({...info, phone: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl" />
                  ) : (
                    <p className="text-[#2D3748] flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" /> {info.phone}</p>
                  )}
                </div>
                <div>
                  <label className="text-sm text-gray-500">Email</label>
                  {isEditing ? (
                    <input type="email" value={info.email} onChange={(e) => setInfo({...info, email: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl" />
                  ) : (
                    <p className="text-[#2D3748] flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" /> {info.email}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="text-sm text-gray-500">Sitio Web</label>
                {isEditing ? (
                  <input type="text" value={info.website} onChange={(e) => setInfo({...info, website: e.target.value})} className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl" />
                ) : (
                  <p className="text-[#2D3748] flex items-center gap-2"><Globe className="w-4 h-4 text-gray-400" /> {info.website || 'No disponible'}</p>
                )}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center">
                  <Home className="w-5 h-5 text-[#36CCD8]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Unidades</p>
                  <p className="text-2xl font-bold text-[#2D3748]">{info.totalUnits ?? '—'}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-[#36CCD8]/10 rounded-xl flex items-center justify-center">
                  <Users className="w-5 h-5 text-[#36CCD8]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Residentes</p>
                  <p className="text-2xl font-bold text-[#2D3748]">{info.totalResidents ?? '—'}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Maintenance fee configuration */}
          {canManageFees && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
              <h3 className="font-bold text-[#2D3748] mb-2">Cuota de Mantenimiento</h3>
              <p className="text-sm text-gray-500 mb-4">Configura cómo se calcula y distribuye la cuota mensual.</p>
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label htmlFor="fee-method" className="text-sm font-semibold text-[#2D3748]">Método de cálculo</label>
                    <select id="fee-method" value={info.fee_calc_method || 'per_unit'} onChange={(e) => setInfo({ ...info, fee_calc_method: e.target.value as CondoInfo['fee_calc_method'] })} className="mt-1 w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                      <option value="per_unit">Por unidad</option>
                      <option value="per_sqm">Por m²</option>
                      <option value="variable">Variable mensual</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">{info.fee_calc_method === 'per_sqm' ? 'La cuota se calcula según los m² de cada unidad' : info.fee_calc_method === 'variable' ? 'Cada mes el administrador ingresa el monto total y se reparte por unidad o por m²' : 'Cada unidad paga la misma cuota'}</p>
                  </div>
                  {(info.fee_calc_method || 'per_unit') !== 'variable' && (
                    <div>
                      <label htmlFor="fee-rate" className={`text-sm font-semibold ${info.fee_calc_method === 'per_sqm' ? 'text-[#C53030]' : 'text-[#2D3748]'}`}>Cuota {info.fee_calc_method === 'per_sqm' ? 'por m²' : 'por unidad'}{info.fee_calc_method === 'per_sqm' ? ' *' : ''}</label>
                      <input id="fee-rate" type="number" min="0" step="0.01" value={info.fee_rate ?? ''} onChange={(e) => setInfo({ ...info, fee_rate: e.target.value === '' ? null : Number(e.target.value) })} className={`mt-1 w-full px-4 py-2 bg-gray-50 border rounded-xl ${info.fee_calc_method === 'per_sqm' && !(Number(info.fee_rate) > 0) ? 'border-red-400 ring-1 ring-red-200' : 'border-gray-200'}`} />
                    </div>
                  )}
                  {info.fee_calc_method === 'variable' && (
                    <div>
                      <label htmlFor="fee-distribution" className="text-sm font-semibold text-[#2D3748]">Distribución *</label>
                      <select id="fee-distribution" required value={info.variable_distribution || ''} onChange={(e) => setInfo({ ...info, variable_distribution: e.target.value as CondoInfo['variable_distribution'] })} className="mt-1 w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl">
                        <option value="" disabled>Selecciona una opción</option>
                        <option value="by_unit">Repartir por unidad</option>
                        <option value="by_area">Repartir por m²</option>
                      </select>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[#2D3748]">{info.fee_calc_method === 'per_sqm' ? `Por m² · ${info.fee_rate ?? '—'}` : info.fee_calc_method === 'variable' ? `Variable mensual · ${info.variable_distribution === 'by_area' ? 'por m²' : 'por unidad'}` : `Por unidad · ${info.fee_rate ?? '—'}`}</p>
              )}
            </div>
          )}

          {/* Admin Info */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h3 className="font-bold text-[#2D3748] mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#36CCD8]" /> Administrador
            </h3>
            <p className="text-[#2D3748]">{info.adminName || 'No disponible'}</p>
          </div>
        </div>

        {/* Save Button */}
        {isEditing && (
          <div className="flex gap-3 mt-6">
            <button 
              onClick={() => setIsEditing(false)} 
              className="flex-1 py-3 border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button 
              onClick={handleSave}
              className="flex-1 py-3 bg-[#36CCD8] text-white font-bold rounded-xl hover:bg-[#2DB8C4]"
            >
              Guardar Cambios
            </button>
          </div>
        )}
      </main>
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
